<template>
  <nav class="breadcrumbs">
    <div class="campsite-breadcrumbs-scroll">
      <BreadcrumbsGenerator
        ref="breadcrumbScrollElement"
        :breadcrumbs="filteredBreadcrumbs"
        :link-all="linkAll"
        @update-rendered-url="({ label, url }) => updateRenderedUrl(label, url)"
      >
        <pu-link
          v-bind="categoriesLink"
          exclude-type="breadcrumb"
          :emit-events="true"
          :non-seo="routeSearchFilters.facets?.length != filteredFacets?.length"
          @url-generated="(url) => updateRenderedUrl('root', url)"
          @click="logGtmEvent"
        >
          <span>{{ rootBreadcrumbName }}</span> </pu-link
        ><span class="crumb">&rsaquo;</span>
        <template #flag>
          <slot name="flag" />
        </template>
      </BreadcrumbsGenerator>
    </div>
    <nuxt-to-meta :nuxt2meta="head" />
  </nav>
</template>

<script setup lang="ts">
import { useI18n } from '~/composables/useI18n'
import { useRoute, useRuntimeConfig } from 'nuxt/app'
import { computed, onUnmounted, ref } from 'vue'
import { routeToFilters } from '~/apps/pu-links/infrastructure/routeToFilters'
import type { BreadcrumbCounts } from '~/composables/useBreadcrumbCounts'
import { useBreadcrumbCounts } from '~/composables/useBreadcrumbCounts'
import type { SearchFilters } from '~/types/search'
import { getFacetRules } from '~/utility/facets'
import { sortOptionsMap } from '~/utility/search'
import { getCampsiteCategoriesPresenter } from '../../apps/categories/presenter-json/CampsiteCategoriesUpdaterJson'
import { CampsiteBreadcrumbsGenerator } from '../../apps/pu-links/breadcrumbs/CampsiteBreadcrumbsGenerator'
import {
  HIERARCHY_COUNTY,
  HIERARCHY_REGION,
  HIERARCHY_TOWN,
  HierarchyPart,
} from '../../apps/pu-links/breadcrumbs/hierarchy-breadcrumbs'
import { SearchLinkGenerator } from '../../apps/url/generators/SearchLinkGenerator'
import { isGlamping } from '../../utility/glamping'
import BreadcrumbsGenerator from './BreadcrumbsGenerator.vue'

const props = withDefaults(
  defineProps<{
    categories: string[]
    hierarchyBreadcrumbs: HierarchyPart[]
    searchFilters?: SearchFilters
    linkAll?: boolean
    withCounts?: boolean
    campsiteDetailUrl?: string
    campsiteName?: string
    removeFilters?: string[]
  }>(),
  {
    linkAll: true,
    withCounts: true,
    campsiteDetailUrl: undefined,
    campsiteName: undefined,
  },
)

const isMounted = ref(true)
onUnmounted(() => {
  isMounted.value = false
})

const route = useRoute()
const config = useRuntimeConfig()
const { $gtm } = useNuxtApp()
const { t } = useI18n()
const facetStore = useFacetsStoreSetup()
const langCode = (route.params.lang as string) || 'en-gb'
const breadcrumbScrollElement = ref<InstanceType<typeof BreadcrumbsGenerator>>()

const breadcrumbsGenerator = new CampsiteBreadcrumbsGenerator()

const routeSearchFilters = computed(() => {
  const filters = routeToFilters(route)
  const hierarchyPath =
    props.hierarchyBreadcrumbs[props.hierarchyBreadcrumbs.length - 1]?.path
  const keysToRemove = new Set(props.removeFilters || [])
  if (filters.sort && !Object.keys(sortOptionsMap).includes(filters.sort)) {
    keysToRemove.add('sort')
  }
  const removals = Array.from(keysToRemove).reduce(
    (acc, key) => ({ ...acc, [key]: undefined }),
    {},
  )
  return {
    ...filters,
    hierarchyPath,
    ...removals,
  } as SearchFilters
})

const isGlampingCategory = computed(() => {
  return isGlamping(routeSearchFilters.value.categoryIds || [])
})

const categoryIds = computed(() => {
  if (isGlampingCategory.value) {
    return []
  }
  return routeSearchFilters.value.categoryIds
})

const filteredFacets = computed(() => {
  return routeSearchFilters.value.facets?.filter((facet) => {
    const shouldRemove = facetRules.value?.some((rule) => {
      if (rule.facet !== facet) return false
      if (rule.category_all) return true
      if (
        rule.category_campsites &&
        !routeSearchFilters.value.categoryIds?.length
      )
        return true
      if (rule.category_glamping && isGlampingCategory.value) return true
      const currentCategoryIds = categoryIds.value || []
      return currentCategoryIds.some((id) =>
        rule.category_ids.includes(Number(id)),
      )
    })
    return !shouldRemove
  })
})

const getSearchLinkGenerator = () => new SearchLinkGenerator()

const categoriesLink = computed(() => {
  const searchLinkGenerator = getSearchLinkGenerator()
  return searchLinkGenerator.getUrlData({
    category: 'campsites',
    filters: { ...routeSearchFilters.value, hierarchyPath: undefined },
    customQueryParams: {
      pitchtype: undefined,
    },
  })
})

const generatedBreadcrumbs = computed(() => {
  return breadcrumbsGenerator.get({
    hierarchies: props.hierarchyBreadcrumbs,
    searchFilters: routeSearchFilters.value,
  })
})

const breadcrumbCountsFilters = computed(() => {
  // Always remove facets to get unfiltered hierarchy counts
  // This determines whether to use SEO format (/-/facet/) or query param (?facet=)
  // based on whether the hierarchy level has > 5 results WITHOUT the facet
  return { ...routeSearchFilters.value, facets: [] }
})

const { breadcrumbCounts } = useBreadcrumbCounts(breadcrumbCountsFilters)

const hierarchyCounts = computed(
  () => (breadcrumbCounts.value as BreadcrumbCounts)?.bookableFacetHierarchy,
)

const { data: facetRules } = await useAsyncData(
  `breadcrumb-facet-rules-${routeSearchFilters.value.facets?.join('-')}`,
  async () => {
    if (!routeSearchFilters.value.facets?.length) {
      return []
    }
    const rules = await Promise.all(
      routeSearchFilters.value.facets.map((facet) =>
        getFacetRules(facet, langCode),
      ),
    )
    return rules.flat()
  },
  {
    server: true,
    lazy: process.client,
    dedupe: 'cancel',
    getCachedData: (key, nuxt) => nuxt.payload.data[key] ?? undefined,
  },
)

const filteredBreadcrumbs = computed(() => {
  return generatedBreadcrumbs.value
    .filter((breadcrumb) => breadcrumb.level !== HIERARCHY_REGION)
    .map((breadcrumb) => {
      const hierarchyCount = breadcrumb.hierarchyPath
        ? hierarchyCounts.value?.[breadcrumb.hierarchyPath]
        : undefined
      const linkable = (hierarchyCount ?? 0) > 0
      const facets = routeSearchFilters.value.facets || []
      const facetRuleMatch = facetRules.value?.some((rule) => {
        if (rule.category_all) return true
        if (
          rule.category_campsites &&
          !routeSearchFilters.value.categoryIds?.length
        )
          return true
        if (rule.category_glamping && isGlampingCategory.value) return true
        const currentCategoryIds = categoryIds.value || []
        return currentCategoryIds.some((id) =>
          rule.category_ids.includes(Number(id)),
        )
      })

      const lowCountCheck = (hierarchyCount ?? 0) < 5 && facets.length

      const nonSeo = facetRuleMatch || lowCountCheck

      let label = breadcrumb.label
      const category = categoryName.value
      if (breadcrumb.level === HIERARCHY_TOWN && facet.value?.name) {
        label = t('{hierarchyTown} {facetName}', {
          hierarchyTown: breadcrumb.label,
          facetName: facet.value?.name.toLocaleLowerCase(),
        })
      } else if (breadcrumb.level === HIERARCHY_COUNTY && category) {
        label = t('{county} {category}', {
          county: breadcrumb.label,
          category,
        })
      }

      return {
        ...breadcrumb,
        linkable,
        nonSeo: Boolean(nonSeo),
        label,
        url: breadcrumb.to?.path || '',
        debugData: {
          hierarchyCount,
          linkable,
          nonSeo: Boolean(nonSeo),
          hierarchyPath: breadcrumb.hierarchyPath,
          level: breadcrumb.level,
        },
      }
    })
})

const renderedUrls = ref(new Map<string, string>())

const updateRenderedUrl = (label: string, url: string) => {
  if (!isMounted.value) return
  renderedUrls.value.set(label, url)
}

interface BreadcrumbItem {
  '@type': 'ListItem'
  position: number
  item: {
    '@id': string
    name: string
  }
}

const generateBreadcrumbItem = (
  label: string,
  path: string,
  position: number,
): BreadcrumbItem => {
  return {
    '@type': 'ListItem',
    position,
    item: {
      '@id': `https://${config.public.envUrl}${path}`,
      name: label,
    },
  }
}

const head = computed(() => {
  const items: BreadcrumbItem[] = []
  if (renderedUrls.value.has('root')) {
    items.push(
      generateBreadcrumbItem(
        rootBreadcrumbName.value,
        renderedUrls.value.get('root') ?? '',
        1,
      ),
    )
  }
  filteredBreadcrumbs.value.forEach((breadcrumb) => {
    if (breadcrumb.linkable && renderedUrls.value.has(breadcrumb.label)) {
      items.push(
        generateBreadcrumbItem(
          breadcrumb.label,
          renderedUrls.value.get(breadcrumb.label) ?? '',
          items.length + 1,
        ),
      )
    }
  })
  if (props.campsiteDetailUrl && props.campsiteName) {
    items.push(
      generateBreadcrumbItem(
        props.campsiteName,
        props.campsiteDetailUrl,
        items.length + 1,
      ),
    )
  }
  return {
    script: [
      {
        hid: 'ldjson-schema-campsite-detail-breadcrumblist',
        type: 'application/ld+json',
        body: true,
        json: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: items,
        },
      },
    ],
  }
})

const facet = computed(() => {
  if (!filteredFacets.value || filteredFacets.value.length > 1) {
    return undefined
  }
  const facetSlug = filteredFacets.value[0]
  const facet = facetStore.facetsBySlug(facetSlug)
  return facet
})

const categoryName = computed(() => {
  if (isGlampingCategory.value) {
    return t('Glamping')
  }
  const categories = categoryIds.value
  const categoriesPresenter = getCampsiteCategoriesPresenter(
    (route.params.lang as string) || 'en-gb',
  )
  if (categories && categories.length === 1) {
    return categoriesPresenter.byIdMap[categories[0]]?.name
  }
  return undefined
})

const rootBreadcrumbName = computed(() => {
  const facetName = facet.value?.name
  const _categoryName = categoryName.value || t('Campsites')
  if (facetName && _categoryName) {
    return t('{categoryName} with {facetName}', {
      facetName: facetName.toLocaleLowerCase(),
      categoryName: _categoryName,
    })
  }
  return _categoryName
})

function logGtmEvent() {
  $gtm.push({
    event: 'breadcrumb_click',
    level: 'root',
  })
}
</script>

<style>
@import url('~/assets/css/breadcrumbs.css');
</style>
